import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import ApiDevice from "../../../app/api/admin/device/ApiDevice";
import Modal from "../../components/modals/modal";
import Button from "../../components/button";
import Select from "../../components/inputs/select";
import InputText from "../../components/inputs/text";
import InputCheckbox from "../../components/inputs/checkbox";
import DateLib from "../../../inc/DateLib";
import StorageLocal from "../../../inc/StorageLocal";

// Выбранные колонки отчёта переживают перезагрузку: набор подбирают один раз под свою задачу.
const COLUMNS_STORAGE_KEY = 'DEVICES_EXPORT_COLUMNS';

// Страница выгрузки — потолок per_page в DevicesController::index.
const EXPORT_PER_PAGE = 200;

// Колонки отчёта: key — стабильный ключ (он и в localStorage), value — текст ячейки из строки
// списка (DeviceResource compact) и справочников. Подписи — человеческие, как на экране, а не
// коды API: отчёт читают в Excel, а не сверяют с бэком. isDefault — отмечена при первом открытии.
const COLUMNS = [
    { key: 'id', label: 'ID', isDefault: true, value: (row) => row.id },
    { key: 'name', label: 'Имя', isDefault: true, value: (row) => row.name },
    { key: 'status', label: 'Состояние', isDefault: true, value: (row, dict) => dict.deviceStatuses[row.status] ?? row.status },
    { key: 'is_manual', label: 'Управление', isDefault: true, value: (row) => (row.is_manual ? 'Ручное' : 'Автоматическое') },
    { key: 'health_status', label: 'Здоровье', value: (row, dict) => (row.health_status ? (dict.deviceHealthStatuses[row.health_status] ?? row.health_status) : '') },
    { key: 'node_name', label: 'Нода', isDefault: true, value: (row) => row.node_name },
    { key: 'device_type_code', label: 'Тариф', isDefault: true, value: (row) => row.device_type_code },
    { key: 'external_id', label: 'ID аккаунта', isDefault: true, value: (row) => row.external_id },
    { key: 'account_login', label: 'Логин аккаунта', isDefault: true, value: (row, dict) => (row.external_id ? (dict.accounts[String(row.external_id)]?.login ?? '') : '') },
    { key: 'proxy', label: 'Прокси', isDefault: true, value: (row, dict) => (row.proxy_id ? (dict.proxies[String(row.proxy_id)] ?? `#${row.proxy_id}`) : '') },
    { key: 'proxy_status', label: 'Статус прокси', value: (row, dict) => (row.proxy_status ? (dict.deviceProxyStatuses[row.proxy_status] ?? row.proxy_status) : '') },
    { key: 'proxy_mode', label: 'Режим прокси', value: (row, dict) => (row.proxy_mode ? (dict.deviceProxyModes[row.proxy_mode] ?? row.proxy_mode) : '') },
    { key: 'adb_port', label: 'ADB-порт', value: (row) => row.adb_port },
    { key: 'provision_version', label: 'Версия провижна', value: (row) => row.provision_version },
    { key: 'last_script', label: 'Последний скрипт', isDefault: true, value: (row) => row.last_script?.name },
    { key: 'last_script_status', label: 'Статус скрипта', value: (row, dict) => (row.last_script ? (dict.scriptStatuses[row.last_script.status] ?? row.last_script.status) : '') },
    { key: 'last_script_ago', label: 'Скрипт запущен', value: (row) => row.last_script?.created_ago },
    { key: 'last_benchmark', label: 'Замер (балл)', value: (row) => row.last_benchmark?.value },
    { key: 'screenshot_at', label: 'Снимок экрана', value: (row) => DateLib.formatterDateTime(row.screenshot_at) },
    { key: 'comment', label: 'Заметка', isDefault: true, value: (row) => row.comment },
];

const SCOPE_OPTIONS_BASE = {
    'filtered': 'Все устройства (с учётом фильтров)',
    'ids': 'По списку ID',
};

// «1, 5, 10-20» → [1, 5, 10, …, 20]. Разделители — запятая, пробел, точка с запятой, перевод
// строки; диапазон через дефис. Мусор возвращаем отдельно, чтобы не выгрузить молча не то.
function parseIds(text) {
    const ids = new Set();
    const invalid = [];
    String(text ?? '').split(/[\s,;]+/).filter(Boolean).forEach((token) => {
        const range = token.match(/^(\d+)-(\d+)$/);
        if (range) {
            const from = Number(range[1]);
            const to = Number(range[2]);
            // Потолок — от опечатки вида 1-100000, а не от реальных диапазонов фермы.
            if (from > to || to - from > 10000) { invalid.push(token); return; }
            for (let id = from; id <= to; id++) ids.add(id);
        } else if (/^\d+$/.test(token)) {
            ids.add(Number(token));
        } else {
            invalid.push(token);
        }
    });
    return { ids: [...ids], invalid };
}

// Ячейка CSV. Разделитель «;» и BOM — под русский Excel: с запятой он склеит всё в одну колонку,
// без BOM прочитает UTF-8 как cp1251. Текст из заметок/имён, начинающийся с = + - @, Excel
// исполнит как формулу — экранируем апострофом (числа не трогаем).
function csvCell(value) {
    if (value === null || value === undefined) return '';
    let text = String(value);
    if (/^[=+\-@]/.test(text) && isNaN(Number(text))) text = `'${text}`;
    return /[";\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCsv(filename, rows) {
    const content = '﻿' + rows.map((cells) => cells.map(csvCell).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

// Все страницы списка разом. params — фильтры и сортировка, как у таблицы.
async function fetchAllDevices(params) {
    const rows = [];
    for (let page = 1; ; page++) {
        const api = ApiDevice.Index({ ...params, per_page: EXPORT_PER_PAGE, page });
        const data = await api.getResponse();
        if (data === null) return null;
        rows.push(...data);
        if (!api.paginationLast || page >= api.paginationLast) return rows;
    }
}

function exportFilename() {
    const pad = (number) => String(number).padStart(2, '0');
    const now = new Date();
    return `devices_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}.csv`;
}

// Выгрузка таблицы устройств в CSV: все (по текущим фильтрам), отмеченные чекбоксами или по
// списку ID — с выбором колонок. Данные берём тем же index(), что и таблица, а не отдельным
// эндпоинтом: подписи аккаунтов/прокси/статусов уже есть во фронтовых справочниках.
export default function DevicesExport({ appliedFilters, selectedIds, accounts, scriptStatuses }) {
    const [_isOpen, set_isOpen] = useState(false);
    return <>
        <Button onClick={() => set_isOpen(true)}>Экспорт</Button>
        {_isOpen ? <DevicesExportModal
            appliedFilters={appliedFilters}
            selectedIds={selectedIds}
            accounts={accounts}
            scriptStatuses={scriptStatuses}
            onClose={() => set_isOpen(false)}
        /> : null}
    </>;
}

function DevicesExportModal({ appliedFilters, selectedIds, accounts, scriptStatuses, onClose }) {
    const [searchParams] = useSearchParams();
    const deviceStatuses = useSelector((state) => state.option.deviceStatuses);
    const proxies = useSelector((state) => state.option.proxies);
    const deviceProxyStatuses = useSelector((state) => state.option.deviceProxyStatuses);
    const deviceProxyModes = useSelector((state) => state.option.deviceProxyModes);
    const deviceHealthStatuses = useSelector((state) => state.option.deviceHealthStatuses);

    const scopeOptions = selectedIds.length > 0
        ? { 'selected': `Отмеченные (${selectedIds.length})`, ...SCOPE_OPTIONS_BASE }
        : SCOPE_OPTIONS_BASE;
    const [_scope, set_scope] = useState(selectedIds.length > 0 ? 'selected' : 'filtered');
    const [_idsText, set_idsText] = useState('');
    const [_columns, set_columns] = useState(() => {
        const saved = StorageLocal.Get(COLUMNS_STORAGE_KEY, null);
        const known = Array.isArray(saved) ? saved.filter((key) => COLUMNS.some((column) => column.key === key)) : [];
        return known.length > 0 ? known : COLUMNS.filter((column) => column.isDefault).map((column) => column.key);
    });
    const [_isLoading, set_isLoading] = useState(false);
    const [_error, set_error] = useState(null);

    const setColumns = (keys) => {
        set_columns(keys);
        StorageLocal.Set(COLUMNS_STORAGE_KEY, keys);
    };
    const toggleColumn = (key, checked) => setColumns(checked
        ? COLUMNS.map((column) => column.key).filter((item) => item === key || _columns.includes(item))
        : _columns.filter((item) => item !== key));

    const handleExport = async () => {
        set_error(null);
        let ids = null;
        if (_scope === 'selected') ids = selectedIds;
        if (_scope === 'ids') {
            const parsed = parseIds(_idsText);
            if (parsed.invalid.length > 0) { set_error(`Не разобрать: ${parsed.invalid.join(', ')}`); return; }
            if (parsed.ids.length === 0) { set_error('Укажите хотя бы один ID'); return; }
            ids = parsed.ids;
        }

        // Сортировка — как у таблицы на экране (?orderBy&orderBySort, см. listProvider).
        const sort = {
            orderBy: searchParams.get('orderBy') ?? 'id',
            orderBySort: searchParams.get('orderBySort') ?? 'asc',
        };

        set_isLoading(true);
        try {
            // По ID — без фильтров таблицы: явно названное устройство не должно выпасть из-за
            // забытого фильтра. Список фермы небольшой, проще забрать весь и отобрать на месте.
            const rows = await fetchAllDevices(ids === null ? { ...appliedFilters, ...sort } : sort);
            if (rows === null) { set_error('Не удалось загрузить устройства'); return; }

            const idSet = ids === null ? null : new Set(ids.map(Number));
            const picked = idSet === null ? rows : rows.filter((row) => idSet.has(Number(row.id)));
            if (picked.length === 0) { set_error('Нет устройств для выгрузки'); return; }

            const dict = { deviceStatuses, proxies, deviceProxyStatuses, deviceProxyModes, deviceHealthStatuses, accounts, scriptStatuses };
            const columns = COLUMNS.filter((column) => _columns.includes(column.key));
            downloadCsv(exportFilename(), [
                columns.map((column) => column.label),
                ...picked.map((row) => columns.map((column) => column.value(row, dict))),
            ]);

            if (idSet !== null && picked.length < idSet.size) {
                const found = new Set(picked.map((row) => Number(row.id)));
                const missing = [...idSet].filter((id) => !found.has(id));
                set_error(`Выгружено ${picked.length}; не найдены ID: ${missing.join(', ')}`);
                return;
            }
            onClose();
        } finally {
            set_isLoading(false);
        }
    };

    return <Modal isOpenModal={true} closeModal={onClose} title="Экспорт устройств" className="devices-export">
        <Select className="input_wf" options={scopeOptions} value={_scope}
            onChange={(e) => set_scope(e.target.value)}>Какие устройства</Select>
        {_scope === 'ids'
            ? <InputText className="input_wf" value={_idsText} placeholder="1, 5, 10-20"
                onChange={(e) => set_idsText(e.target.value)}>ID устройств</InputText>
            : null}

        <div className="devices-export__columns-head">
            <span>Колонки</span>
            <button type="button" className="devices-export__link"
                onClick={() => setColumns(COLUMNS.map((column) => column.key))}>все</button>
            <button type="button" className="devices-export__link" onClick={() => setColumns([])}>ничего</button>
            <button type="button" className="devices-export__link"
                onClick={() => setColumns(COLUMNS.filter((column) => column.isDefault).map((column) => column.key))}>по умолчанию</button>
        </div>
        <div className="devices-export__columns">
            {COLUMNS.map((column) => <InputCheckbox key={column.key} value={_columns.includes(column.key)}
                onChange={(e) => toggleColumn(column.key, e.target.checked)}>{column.label}</InputCheckbox>)}
        </div>

        {_error ? <div className="devices-export__error">{_error}</div> : null}

        <div className="devices-export__actions">
            <Button className="btn-success" disabled={_isLoading || _columns.length === 0} onClick={handleExport}>
                {_isLoading ? 'Готовлю…' : 'Скачать CSV'}
            </Button>
            <Button onClick={onClose}>Отмена</Button>
        </div>
    </Modal>;
}
